import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { Plus, Trash2, Pencil, Mail, Users } from 'lucide-react'
import ClientWizard from './ClientWizard'
import { useConfirm, deleteJSON } from './ConfirmDialog'
import { ErrorState } from './ViewState'
import { useWS } from '../contexts/WSContext'

// Administração › Clientes: lista + wizard de cadastro/edição.
export default function AdminClients() {
  const { t } = useTranslation()
  const { mayEdit, mayDelete, reasonEdit, reasonDelete } = useRegistryPerms()
  const { clients, mines, adminError, reloadAdmin } = useWS()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [confirmDialog, ask] = useConfirm()

  const openCreate = () => { setEditing(null); setOpen(true) }
  const openEdit = (c) => { setEditing(c); setOpen(true) }

  const minesOf = (c) => mines.filter(m => m.client_id === c.id)

  // O backend recusa apagar cliente com minas (500 com o erro do banco): o diálogo
  // mostra a mensagem em vez de deixar o cartão parado convidando a um segundo clique.
  const remove = (c) => ask({
    title: t('admin:clients.delete_title'),
    message: t('admin:clients.delete_message', { name: c.name, mines: minesOf(c).length }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/clients/${c.id}`); reloadAdmin() },
  })

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin:clients.count', { count: clients.length })}</p>
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand text-white hover:bg-brand-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={16} /> {t('admin:clients.add')}
        </button>
      </div>

      {adminError ? (
        <ErrorState message={adminError} onRetry={reloadAdmin} />
      ) : clients.length === 0 ? (
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="w-full flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={28} />
          <span className="text-sm">{t('admin:clients.empty_cta')}</span>
        </button>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {clients.map(c => {
            const admins = Array.isArray(c.admin_accounts) ? c.admin_accounts : []
            const cMines = minesOf(c)
            return (
              <div key={c.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">{c.name}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    <button disabled={!mayEdit} onClick={() => openEdit(c)} title={!mayEdit ? t(reasonEdit) : t('common:actions.edit')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={15} /></button>
                    <button disabled={!mayDelete} onClick={() => remove(c)} title={!mayDelete ? t(reasonDelete) : t('admin:actions.remove')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="mt-2 space-y-1 text-xs text-slate-500 dark:text-slate-400">
                  {c.email && <p className="flex items-center gap-1.5 truncate"><Mail size={12} className="text-slate-400 dark:text-slate-500" /> {c.email}</p>}
                  <p className="flex items-center gap-1.5"><Users size={12} className="text-slate-400 dark:text-slate-500" /> {t('admin:clients.accounts_mines', { admins: admins.length, mines: cMines.length })}</p>
                </div>
                {admins.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {admins.map((a, i) => (
                      <span key={i} title={a.email} className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[11px] text-slate-600 dark:text-slate-300">{a.name}</span>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <ClientWizard key={editing?.id ?? 'new'} open={open} onClose={() => setOpen(false)} editing={editing} />
      {confirmDialog}
    </div>
  )
}
